import {Injectable} from '@angular/core';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {GermanAddress} from '@angular-material-extensions/google-maps-autocomplete';
import {Landlord} from '@landlords/models';
import {Property} from '@properties/models/property.interface';
import {PropertiesService} from '@properties/services/properties.service';
import type {PublicProfileStationeryTemplate} from '@settings/models/publicProfile.settings.interface';
import {AuthService} from '@shared/services/auth.service';
import {User} from '@users/models/user.interface';
import {UsersService} from '@users/services/users.service';
import {computeDueDate} from '@vat/engine/deadlines';
import {areaShareForMonth, buildRuleContext, summarizeUnitAreasForMonth} from '@vat/engine/index';
import {monthKeysOfPeriod, rangeOfPeriod} from '@vat/engine/month';
import {createProfileAt, resolveTaxProfile} from '@vat/engine/tax-profile';
import {BookingLite, ENGINE_VERSION, ExpenseLite, LandlordTaxProfile, VatAdjustment, VatLine} from '@vat/engine/types';
import {formatAddress} from 'app/helpers/address';
import {DatevPaymentsMappingService} from 'app/modules/admin/booking/features/payments/services/datev-payments-mapping.service';
import {getLandlordDisplayName} from 'app/modules/admin/properties/features/units/pipes/landlordDisplayName.pipe';
import {ExportBottomSheetComponent} from 'app/shared/components/export-bottom-sheet/export-bottom-sheet.component';
import {firstValueFrom} from 'rxjs';
import {take} from 'rxjs/operators';
import {periodKeysFor} from '../components/cockpit/vat-cockpit.util';
import {VatDeadlineSheetPeriod, VatDeadlineTasksSheetComponent, VatDeadlineTasksSheetData} from '../components/return/vat-deadline-tasks-sheet.component';
import {VatStationeryDialogComponent, VatStationeryDialogData} from '../components/return/vat-stationery-dialog.component';
import {VatTransmittedDialogComponent, VatTransmittedDialogResult} from '../components/return/vat-transmitted-dialog.component';
import {VatObjektDialogComponent, VatObjektDialogData, VatObjektDialogResult, VatObjektPeriodOption} from '../components/shared/vat-objekt-dialog.component';
import {sha256Hex} from '../export/sha256';
import {buildVatExportEntry} from '../export/vat-export-entry';
import {buildKz48SheetData, buildVatExportSheetData, hasReleasedSnapshot, resolveVatExportAction, VatExportSelection} from '../export/vat-export-reports';
import {VatExportLandlord, VatExportPropertyLabel, VatExportSnapshot, VatObjektExportFormat, VatReturn} from '../export/vat-export.types';
import {formatDateDE, periodLabel} from '../export/vat-format';
import {buildKz48MonthRow, Kz48MonthRow, Kz48NachweisInput} from '../export/vat-kz48-nachweis';
import {buildVatMailBody, buildVatMailSubject} from '../export/vat-mail-draft';
import {buildObjektAbrechnung, ObjektAbrechnungInput, ObjektAbrechnungPeriod, ObjektAreaMonth, ObjektUnitRow} from '../export/vat-objekt-abrechnung';
import {VAT_DEADLINE_COPY, VAT_EXPORT_COPY, VAT_MAIL_COPY, VAT_OBJEKT_COPY, VAT_OBJEKT_DIALOG_COPY, VAT_TRANSMITTED_COPY} from '../vat-copy';
import {VatComputeService} from './vat-compute.service';
import {VatDataLoaderService} from './vat-data-loader.service';
import {VatDeadlineRecipient} from './vat-deadline-tasks.logic';
import {VatExportService} from './vat-export.service';
import {VatMailService} from './vat-mail.service';
import {VatReturnWorkflowService, WorkflowContext} from './vat-return-workflow.service';
import {activeReturnForPeriod} from './vat-return-workflow.util';
import {VatReturnsService} from './vat-returns.service';
import {VatTaxProfileService} from './vat-tax-profile.service';

/** Aufruf der Objekt-Abrechnung aus Cockpit, Landing, Sidebar-Deep-Link und Export-Sheet (Spec 2.4). */
export interface VatObjektAbrechnungParams {
    landlordID: string;
    year: number;
    /** Vorbelegter Voranmeldungszeitraum; null = Kalenderjahr vorbelegt. */
    periodKey: string | null;
    propertyID?: string;
    format?: VatObjektExportFormat;
    saveToFolder?: boolean;
}

const MONTH_KEYS = ['M01', 'M02', 'M03', 'M04', 'M05', 'M06', 'M07', 'M08', 'M09', 'M10', 'M11', 'M12'];
const QUARTER_KEYS = ['Q1', 'Q2', 'Q3', 'Q4'];

/**
 * Einzige Schnittstelle von Plan 3 zu den Exporten (A1): Export-Sheet, Steuerberater-Mail, Übermittlungs-Marker,
 * Fristen-Aufgaben, Kz-48-Nachweis, Objekt-Abrechnung (Stufe 2, Task C2). Nur diese Datei kennt
 * VatReturnWorkflowService, VatComputeService und VatDataLoaderService. Route-Provider am `umsatzsteuer`-Eintrag (A12).
 *
 * Abweichung zu Plan 4 A1: Plan 3 liefert `markExported(returnID, context, entry)` und
 * `markUebermittelt(returnID, context, am, transferticket)` mit `WorkflowContext`; der Engine-Cache
 * `VatComputeService.compute(returnKey, input)` ist synchron, die Kz-48-Monatswerte entstehen deshalb wie im
 * Cockpit über `VatDataLoaderService.load` + `buildEngineInput` + `compute`.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Injectable()
export class VatExportFacadeService {

    constructor(
        private bottomSheet: MatBottomSheet,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private authService: AuthService,
        private exportService: VatExportService,
        private mailService: VatMailService,
        private workflow: VatReturnWorkflowService,
        private compute: VatComputeService,
        private loader: VatDataLoaderService,
        private taxProfileService: VatTaxProfileService,
        private vatReturnsService: VatReturnsService,
        private propertiesService: PropertiesService,
        private usersService: UsersService,
        private datevMappingService: DatevPaymentsMappingService,
    ) {
    }

    // ---------------------------------------------------------------------------------------------
    // Exportieren
    // ---------------------------------------------------------------------------------------------

    async openExportSheet(vatReturn: VatReturn): Promise<void> {
        if (!hasReleasedSnapshot(vatReturn)) {
            this.snackBar.open(VAT_EXPORT_COPY.notReleased, 'OK', {duration: 6000});
            return;
        }
        const selection = await firstValueFrom(this.bottomSheet.open(ExportBottomSheetComponent, {data: buildVatExportSheetData(vatReturn)}).afterDismissed()) as VatExportSelection | undefined;
        const action = resolveVatExportAction(selection);
        if (!action) {
            return;
        }
        if (action === 'KZ48_PDF') {
            return this.exportKz48Nachweis(vatReturn.landlordID, vatReturn.year, !!selection.saveToFolder);
        }
        if (action === 'OBJEKT_PDF' || action === 'OBJEKT_EXCEL') {
            return this.exportObjektAbrechnung({
                landlordID: vatReturn.landlordID, year: vatReturn.year, periodKey: vatReturn.periodKey,
                format: action === 'OBJEKT_EXCEL' ? 'excel' : 'pdf', saveToFolder: !!selection.saveToFolder,
            });
        }
        try {
            const {snapshot, landlord} = await this.buildSnapshot(vatReturn);
            if (!snapshot.lines.length) {
                this.snackBar.open(VAT_EXPORT_COPY.noLines, 'OK', {duration: 5000});
            }
            const result = action === 'VORDRUCK_PDF'
                ? await this.exportService.exportVordruckPdf(snapshot, await this.pickStationery(landlord), !!selection.saveToFolder)
                : await this.exportService.exportHerleitungExcel(snapshot, !!selection.saveToFolder);
            await this.workflow.markExported(vatReturn.id, this.contextOf(vatReturn, snapshot.landlord.name), result.entry);
            this.snackBar.open(VAT_EXPORT_COPY.success(result.fileName), 'OK', {duration: 5000});
        } catch (error) {
            console.error('Export der Voranmeldung fehlgeschlagen -->', error);
            this.snackBar.open(VAT_EXPORT_COPY.error, 'OK', {duration: 7000});
        }
    }

    /**
     * Kz-48-Jahresnachweis (A4): zwölf Monate live über Loader + Engine-Cache, ohne exports[]/Activity.
     * Monate, die noch nicht begonnen haben, werden nicht geladen (Muster Cockpit), sondern als Null-Zeile geführt.
     */
    async exportKz48Nachweis(landlordID: string, year: number, saveToFolder?: boolean): Promise<void> {
        let toFolder = saveToFolder;
        if (toFolder === undefined) {
            const selection = await firstValueFrom(this.bottomSheet.open(ExportBottomSheetComponent, {data: buildKz48SheetData()}).afterDismissed()) as VatExportSelection | undefined;
            if (resolveVatExportAction(selection) !== 'KZ48_PDF') {
                return;
            }
            toFolder = !!selection.saveToFolder;
        }
        try {
            const landlord = await this.loadLandlord(landlordID);
            const taxProfile = await this.loadTaxProfile(landlordID);
            const properties = await this.loadPropertyLabels(landlordID);
            const now = new Date();
            const profileAt = createProfileAt(taxProfile);
            const months: Kz48MonthRow[] = [];
            for (const [index, periodKey] of MONTH_KEYS.entries()) {
                const monthKey = `${year}-${periodKey.slice(1)}`;
                const range = rangeOfPeriod(year, periodKey);
                if (range.start.getTime() > now.getTime()) {
                    months.push({monthKey, kz48: 0, lineCount: 0, byProperty: {}});
                    continue;
                }
                this.snackBar.open(VAT_EXPORT_COPY.progress(index + 1, MONTH_KEYS.length), undefined, {duration: 1500});
                const data = await this.loader.load(landlordID, range, profileAt(range.start).status, now);
                const entry = this.compute.compute(
                    `${landlordID}_${year}_${periodKey}_nachweis`,
                    this.loader.buildEngineInput(data, {landlordID, range, profile: taxProfile, adjustments: [], now}),
                );
                months.push(buildKz48MonthRow(monthKey, entry.result));
            }
            const input: Kz48NachweisInput = {landlord: this.toExportLandlord(landlord), taxProfile, year, months, properties, engineVersion: ENGINE_VERSION, now: new Date()};
            const {fileName} = await this.exportService.exportKz48Pdf(input, toFolder);
            this.snackBar.open(VAT_EXPORT_COPY.success(fileName), 'OK', {duration: 5000});
        } catch (error) {
            console.error('Kz-48-Nachweis fehlgeschlagen -->', error);
            this.snackBar.open(VAT_EXPORT_COPY.error, 'OK', {duration: 7000});
        }
    }

    // ---------------------------------------------------------------------------------------------
    // Umsatzsteuerabrechnung je Objekt (Spec 2.5, Task C2)
    // ---------------------------------------------------------------------------------------------

    /**
     * Objekt-Abrechnung: Vor-Dialog (Immobilie, Zeitraum, Format, Ablage), dann je Zeitraumschlüssel der Auswahl
     * (1 bei Monat/Quartal, 4 oder 12 beim Kalenderjahr) Loader + Adjustments des aktiven Dokuments + Engine-Cache;
     * Zeilen der Immobilie, Flächen je Monat über den RuleContext, Einheiten zum letzten Monat. Nur Regelbesteuerung im
     * ABZURECHNENDEN Jahr (nicht `taxProfile.status`, der ist die Kopie des heutigen Eintrags) – sonst Hinweis auf den
     * Kz-48-Nachweis. Kein VatReturn, kein exports[] (Muster Kz 48); Datei hängt an der Immobilie.
     */
    async exportObjektAbrechnung(params: VatObjektAbrechnungParams): Promise<void> {
        try {
            const [landlord, taxProfile, properties] = await Promise.all([
                this.loadLandlord(params.landlordID),
                this.loadTaxProfile(params.landlordID),
                this.loadPropertyLabels(params.landlordID),
            ]);
            // Profil zum abzurechnenden Jahr: Status UND Voranmeldungszeitraum sind je Abschnitt historisiert – nach einem
            // Wechsel zu Kleinunternehmer behält das Altjahr seine Zeiträume (Profilwert wäre BEFREIT).
            const yearProfile = resolveTaxProfile(taxProfile, new Date(params.year, 0, 1));
            const keys = periodKeysFor(yearProfile);
            if (!this.isRegelbesteuertImJahr(yearProfile, params.year, keys)) {
                this.snackBar.open(VAT_OBJEKT_DIALOG_COPY.notRegelbesteuerung, 'OK', {duration: 8000});
                return;
            }
            if (!keys.length) {
                this.snackBar.open(VAT_OBJEKT_DIALOG_COPY.noPeriods, 'OK', {duration: 6000});
                return;
            }
            const periodOptions: VatObjektPeriodOption[] = [
                {key: null, label: VAT_OBJEKT_COPY.header.kalenderjahr(params.year)},
                ...keys.map(key => ({key, label: periodLabel(params.year, key)})),
            ];
            const preselectKey = params.periodKey && keys.includes(params.periodKey) ? params.periodKey : null;
            const data: VatObjektDialogData = {
                properties,
                periodOptions,
                preselect: {
                    propertyID: params.propertyID ?? null, periodKey: preselectKey,
                    ...(params.format ? {format: params.format} : {}),
                    ...(params.saveToFolder !== undefined ? {saveToFolder: params.saveToFolder} : {}),
                },
            };
            const result = await firstValueFrom(this.dialog.open<VatObjektDialogComponent, VatObjektDialogData, VatObjektDialogResult | null>(VatObjektDialogComponent, {
                width: '600px', maxWidth: '95vw', autoFocus: false, data,
            }).afterClosed()) as VatObjektDialogResult | null | undefined;
            if (!result?.propertyID) {
                return;
            }
            const property = properties.find(p => p.id === result.propertyID);
            if (!property) {
                this.snackBar.open(VAT_OBJEKT_DIALOG_COPY.propertyMissing, 'OK', {duration: 6000});
                return;
            }
            const input = await this.buildObjektInput({landlordID: params.landlordID, year: params.year, periodKey: result.periodKey, keys, landlord, taxProfile: yearProfile, property});
            const {fileName} = await this.exportService.exportObjekt(buildObjektAbrechnung(input), input, result.format, result.saveToFolder, property.id);
            this.snackBar.open(VAT_EXPORT_COPY.success(fileName), 'OK', {duration: 5000});
        } catch (error) {
            console.error('Objekt-Abrechnung fehlgeschlagen -->', error);
            this.snackBar.open(VAT_EXPORT_COPY.error, 'OK', {duration: 7000});
        }
    }

    /**
     * Berechnung nach Spec 2.5 – Kalenderjahr = Schleife über alle Zeitraumschlüssel des Profils (Zukunft übersprungen).
     *
     * Kein Status-Skip je Zeitraum: `profileAt` wechselt nur zum Jahreswechsel (`abJahr`), nach dem Jahres-Gate in
     * `exportObjektAbrechnung` sind also alle Zeiträume des Jahres regelbesteuert. Selbst ohne Gate lieferte die Engine für
     * Kleinunternehmer-/steuerfreie Monate nur KU_ALTUMSATZ (Kz NONE) bzw. Kz 48 und keine Vorsteuer (rules/soll.ts, rules/vorsteuer.ts).
     */
    private async buildObjektInput(p: {
        landlordID: string; year: number; periodKey: string | null; keys: string[];
        landlord: Landlord; taxProfile: LandlordTaxProfile; property: VatExportPropertyLabel;
    }): Promise<ObjektAbrechnungInput> {
        const now = new Date();
        const profileAt = createProfileAt(p.taxProfile);
        const isYear = p.periodKey === null;
        const runKeys = isYear ? p.keys : [p.periodKey];
        const docs = await firstValueFrom(this.vatReturnsService.listByLandlordAndYear(p.landlordID, p.year).pipe(take(1)));

        const lines: VatLine[] = [];
        const bookingsByID: Record<string, BookingLite> = {};
        const expensesByID: Record<string, ExpenseLite> = {};
        const paymentBookingIDs: Record<string, string> = {};
        const areaMonths: ObjektAreaMonth[] = [];
        let units: ObjektUnitRow[] = [];

        for (const [index, key] of runKeys.entries()) {
            const range = rangeOfPeriod(p.year, key);
            if (isYear && range.start.getTime() > now.getTime()) {
                continue;
            }
            this.snackBar.open(VAT_OBJEKT_DIALOG_COPY.progress(index + 1, runKeys.length), undefined, {duration: 1500});
            const adjustments: VatAdjustment[] = activeReturnForPeriod(docs ?? [], key)?.adjustments ?? [];
            let data = await this.loader.load(p.landlordID, range, profileAt(range.start).status, now);
            if (adjustments.length) {
                data = await this.loader.ensureAdjustmentSources(data, adjustments);
            }
            const engineInput = this.loader.buildEngineInput(data, {landlordID: p.landlordID, range, profile: p.taxProfile, adjustments, now});
            const entry = this.compute.compute(`objekt_${p.landlordID}_${p.year}_${key}`, engineInput);

            lines.push(...entry.result.lines.filter(line => line.source.propertyID === p.property.id));
            data.bookings.forEach(booking => bookingsByID[booking.id] = booking);
            data.expenses.forEach(expense => expensesByID[expense.id] = expense);
            [...data.payments, ...data.priorPayments].forEach(payment => {
                if (payment.bookingID) {
                    paymentBookingIDs[payment.id] = payment.bookingID;
                }
            });

            const ctx = buildRuleContext(engineInput);
            const monthKeys = monthKeysOfPeriod(p.year, key);
            monthKeys.forEach(monthKey => areaMonths.push({monthKey, ...areaShareForMonth(ctx, p.property.id, monthKey)}));
            const lastMonth = monthKeys[monthKeys.length - 1];
            units = summarizeUnitAreasForMonth(ctx, p.property.id, lastMonth)
                .map(({unitID, label, categoryType, tenantsName, area, taxable, vacant}): ObjektUnitRow => ({unitID, label, categoryType, tenantsName, area, taxable, vacant}));
        }

        const period: ObjektAbrechnungPeriod = isYear
            ? {year: p.year, periodKey: null, label: VAT_OBJEKT_COPY.header.kalenderjahr(p.year), start: rangeOfPeriod(p.year, p.keys[0]).start, end: rangeOfPeriod(p.year, p.keys[p.keys.length - 1]).end}
            : {year: p.year, periodKey: p.periodKey, label: periodLabel(p.year, p.periodKey), start: rangeOfPeriod(p.year, p.periodKey).start, end: rangeOfPeriod(p.year, p.periodKey).end};

        return {
            landlord: this.toExportLandlord(p.landlord),
            taxProfile: p.taxProfile,
            property: p.property,
            period,
            lines,
            bookingsByID,
            paymentBookingIDs,
            expensesByID,
            kontoMapping: await this.datevMappingService.loadMapping(),
            areaMonths,
            units,
            engineVersion: ENGINE_VERSION,
            now: new Date(),
        };
    }

    // ---------------------------------------------------------------------------------------------
    // An Steuerberater übergeben (nur Entwurf)
    // ---------------------------------------------------------------------------------------------

    async openTaxAdvisorMail(vatReturn: VatReturn): Promise<void> {
        if (!hasReleasedSnapshot(vatReturn)) {
            this.snackBar.open(VAT_EXPORT_COPY.notReleased, 'OK', {duration: 6000});
            return;
        }
        const gate = this.mailService.resolveGate();
        if (gate === 'UPGRADE') {
            this.mailService.openUpgradeDialog();
            return;
        }
        if (gate === 'NO_PERMISSION') {
            const ref = this.snackBar.open(VAT_MAIL_COPY.noPermission, VAT_MAIL_COPY.downloadFallback, {duration: 10000});
            ref.onAction().pipe(take(1)).subscribe(() => void this.downloadBoth(vatReturn));
            return;
        }
        try {
            const {snapshot, landlord, taxProfile} = await this.buildSnapshot(vatReturn);
            const steuerberater = vatReturn.taxProfileSnapshot?.steuerberater ?? taxProfile?.steuerberater ?? null;
            if (!steuerberater?.email) {
                this.snackBar.open(VAT_MAIL_COPY.noRecipient, 'OK', {duration: 8000});
                return;
            }
            this.snackBar.open(VAT_MAIL_COPY.preparing, undefined, {duration: 3000});
            const context = this.contextOf(vatReturn, snapshot.landlord.name);
            const attachments = await this.exportService.buildMailAttachments(snapshot, await this.pickStationery(landlord));
            await this.workflow.markExported(vatReturn.id, context, attachments.pdf.entry);
            await this.workflow.markExported(vatReturn.id, context, attachments.xlsx.entry);

            const label = periodLabel(vatReturn.year, vatReturn.periodKey);
            const due = this.dueDateFor(vatReturn.year, vatReturn.periodKey, taxProfile);
            const subject = buildVatMailSubject({periodLabelText: label, landlordName: snapshot.landlord.name});
            const body = buildVatMailBody({
                landlordName: snapshot.landlord.name, steuerberaterName: steuerberater.name || null, periodLabelText: label, version: vatReturn.version,
                kennzahlen: vatReturn.kennzahlen, attachments: attachments.files.map(f => f.name), dueDateLabel: due ? formatDateDE(due) : null,
            });
            const dialogRef = this.mailService.openTaxAdvisorDraft({landlordID: vatReturn.landlordID, steuerberater, subject, body, files: attachments.files});
            dialogRef?.afterClosed().pipe(take(1)).subscribe(async (result: any) => {
                // Nur ein gesendetes NahausMessageContent (Objekt) zählt als Übergabe; undefined/'cancelled' nicht.
                if (result && typeof result === 'object' && !Array.isArray(result)) {
                    try {
                        const recipients: string[] = Array.isArray(result?.email?.to) ? result.email.to : [];
                        const entry = buildVatExportEntry({type: 'MAIL', fileName: subject, filePath: null, url: null, sha256: await sha256Hex(`${subject}\n${recipients.join(',')}`), uid: this.uid(), now: new Date()});
                        await this.workflow.markExported(vatReturn.id, context, entry);
                    } catch (error) {
                        console.error('Mail-Übergabe konnte nicht protokolliert werden -->', error);
                    }
                }
            });
        } catch (error) {
            console.error('Steuerberater-Mail konnte nicht vorbereitet werden -->', error);
            this.snackBar.open(VAT_EXPORT_COPY.error, 'OK', {duration: 7000});
        }
    }

    /** Fallback ohne OUTBOX_WRITE (Spec 7): beide Dateien herunterladen und protokollieren. */
    private async downloadBoth(vatReturn: VatReturn): Promise<void> {
        try {
            const {snapshot, landlord} = await this.buildSnapshot(vatReturn);
            const context = this.contextOf(vatReturn, snapshot.landlord.name);
            const pdf = await this.exportService.exportVordruckPdf(snapshot, await this.pickStationery(landlord), false);
            await this.workflow.markExported(vatReturn.id, context, pdf.entry);
            const xlsx = await this.exportService.exportHerleitungExcel(snapshot, false);
            await this.workflow.markExported(vatReturn.id, context, xlsx.entry);
        } catch (error) {
            console.error('Download der Übergabe-Dateien fehlgeschlagen -->', error);
            this.snackBar.open(VAT_EXPORT_COPY.error, 'OK', {duration: 7000});
        }
    }

    // ---------------------------------------------------------------------------------------------
    // Als übermittelt markieren
    // ---------------------------------------------------------------------------------------------

    async openMarkTransmittedDialog(vatReturn: VatReturn): Promise<void> {
        const result = await firstValueFrom(this.dialog.open(VatTransmittedDialogComponent, {
            width: '480px', data: {periodLabelText: periodLabel(vatReturn.year, vatReturn.periodKey)},
        }).afterClosed()) as VatTransmittedDialogResult | null | undefined;
        if (!result) {
            return;
        }
        try {
            const landlord = await this.loadLandlord(vatReturn.landlordID);
            await this.workflow.markUebermittelt(vatReturn.id, this.contextOf(vatReturn, this.landlordName(landlord) || null), result.am, result.transferticket);
            this.snackBar.open(VAT_TRANSMITTED_COPY.success, 'OK', {duration: 5000});
        } catch (error) {
            console.error('Übermittlungs-Marker fehlgeschlagen -->', error);
            this.snackBar.open(VAT_TRANSMITTED_COPY.error, 'OK', {duration: 7000});
        }
    }

    // ---------------------------------------------------------------------------------------------
    // Aufgabe anlegen
    // ---------------------------------------------------------------------------------------------

    async openDeadlineTasksSheet(params: { landlordID: string; year: number }): Promise<void> {
        const uid = this.uid();
        if (!uid) {
            this.snackBar.open(VAT_DEADLINE_COPY.noUser, 'OK', {duration: 6000});
            return;
        }
        const landlord = await this.loadLandlord(params.landlordID);
        const taxProfile = await this.loadTaxProfile(params.landlordID);
        if (taxProfile.voranmeldungszeitraum === 'BEFREIT') {
            this.snackBar.open(VAT_DEADLINE_COPY.noPeriods, 'OK', {duration: 6000});
            return;
        }
        const existing = await firstValueFrom(this.vatReturnsService.listByLandlordAndYear(params.landlordID, params.year).pipe(take(1)));
        const latestByPeriod = new Map<string, VatReturn>();
        (existing ?? []).forEach(r => {
            const current = latestByPeriod.get(r.periodKey);
            if (!current || r.version > current.version) {
                latestByPeriod.set(r.periodKey, r);
            }
        });
        const now = new Date();
        const keys = taxProfile.voranmeldungszeitraum === 'MONAT' ? MONTH_KEYS : QUARTER_KEYS;
        const periods: VatDeadlineSheetPeriod[] = keys.map(periodKey => {
            const due = computeDueDate({year: params.year, periodKey, dauerfrist: taxProfile.dauerfristverlaengerung, bundesland: taxProfile.bundesland, now});
            const existingReturn = latestByPeriod.get(periodKey) ?? null;
            return {
                year: params.year, periodKey, dueDate: due.dueDate, dueDateEffective: due.dueDateEffective, regionHint: due.regionHint, existingReturn,
                label: periodLabel(params.year, periodKey), dueLabel: formatDateDE(due.dueDateEffective),
                hasTask: !!existingReturn?.fristen?.taskID, isOverdue: due.istUeberfaellig,
            };
        });
        const data: VatDeadlineTasksSheetData = {
            landlordID: params.landlordID, landlordName: this.landlordName(landlord), customerID: this.authService.customerID ?? null,
            currentUserID: uid, periods, users: await this.loadTeamUsers(),
        };
        await firstValueFrom(this.bottomSheet.open(VatDeadlineTasksSheetComponent, {data}).afterDismissed());
    }

    // ---------------------------------------------------------------------------------------------
    // Stammdaten und Snapshot
    // ---------------------------------------------------------------------------------------------

    private async buildSnapshot(vatReturn: VatReturn): Promise<{ snapshot: VatExportSnapshot; landlord: Landlord; taxProfile: LandlordTaxProfile }> {
        const [lines, landlord, taxProfile, properties] = await Promise.all([
            this.exportService.loadLines(vatReturn.id),
            this.loadLandlord(vatReturn.landlordID),
            this.loadTaxProfile(vatReturn.landlordID),
            this.loadPropertyLabels(vatReturn.landlordID),
        ]);
        return {snapshot: {vatReturn, lines, landlord: this.toExportLandlord(landlord), properties, now: new Date()}, landlord, taxProfile};
    }

    /** Kontext für Activities (lokal + Kunden-Feed) des Plan-3-Workflows. */
    private contextOf(vatReturn: VatReturn, landlordsName: string | null): WorkflowContext {
        return {landlordID: vatReturn.landlordID, landlordsName: landlordsName || null, year: vatReturn.year, periodKey: vatReturn.periodKey};
    }

    private loadLandlord(landlordID: string): Promise<Landlord> {
        return firstValueFrom(this.taxProfileService.landlord$(landlordID).pipe(take(1)));
    }

    private loadTaxProfile(landlordID: string): Promise<LandlordTaxProfile> {
        return firstValueFrom(this.taxProfileService.taxProfile$(landlordID).pipe(take(1)));
    }

    /**
     * Gate der Objekt-Abrechnung auf das abzurechnende Jahr: regelbesteuert, wenn mindestens ein Zeitraumbeginn des Jahres
     * laut `besteuerung`-Historie Regelbesteuerung ist (`profileAt` wechselt nur per `abJahr`). Ohne Zeitraumschlüssel
     * (BEFREIT) entscheiden die Monatsanfänge – so erhalten steuerfrei/Kleinunternehmer den Kz-48-Hinweis statt `noPeriods`.
     */
    private isRegelbesteuertImJahr(taxProfile: LandlordTaxProfile, year: number, keys: string[]): boolean {
        const profileAt = createProfileAt(taxProfile);
        return (keys.length ? keys : MONTH_KEYS).some(key => profileAt(rangeOfPeriod(year, key).start).status === 'REGELBESTEUERUNG');
    }

    /** getLandlordDisplayName liefert bei Firmen ohne Vor-/Nachnamen ein führendes Leerzeichen → trim (geprüft 11.09.2026). */
    private landlordName(landlord: Landlord): string {
        return (getLandlordDisplayName(landlord) ?? '').trim();
    }

    private toExportLandlord(landlord: Landlord): VatExportLandlord {
        return {name: this.landlordName(landlord), addressLine: formatAddress(landlord?.address as GermanAddress)};
    }

    /** Immobilien des Eigentümers als „H{h_id} · Adresse“ (nur Labels – keine Steuerdaten). */
    private async loadPropertyLabels(landlordID: string): Promise<VatExportPropertyLabel[]> {
        const query = await firstValueFrom(this.propertiesService.collection(ref => ref.where('landlord.id', '==', landlordID)).get({source: 'server'}));
        return query.docs
            .map(doc => ({...(doc.data() as Property), id: doc.id}))
            .map(property => ({
                id: property.id,
                label: [
                    property.h_id !== undefined && property.h_id !== null ? `H${property.h_id}` : '',
                    formatAddress(property.address as GermanAddress),
                ].filter(Boolean).join(' · ') || property.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, 'de'));
    }

    /** Team (Muster create-reminder-dialog): sortiert nach displayName, deaktivierte Nutzer ausgeblendet. */
    private async loadTeamUsers(): Promise<VatDeadlineRecipient[]> {
        const query = await firstValueFrom(this.usersService.collection(ref => ref.orderBy('displayName').limit(30)).get());
        if (query.empty) {
            return [];
        }
        return query.docs
            .map(doc => doc.data() as User)
            .filter(user => !!user?.id && !user.disabled)
            .map(user => ({id: user.id, displayName: user.displayName || user.email || user.id, email: user.email ?? null}));
    }

    /** Briefpapier nur bei vorhandenen LETTER-Vorlagen des Eigentümers abfragen (A6). */
    private async pickStationery(landlord: Landlord): Promise<PublicProfileStationeryTemplate | null> {
        const templates = (landlord?.stationeryTemplates ?? []).filter(t => (t as any)?.scopes?.includes('LETTER'));
        if (!templates.length) {
            return null;
        }
        const data: VatStationeryDialogData = {templates, ownerId: landlord.id, activeLetterheadType: landlord.activeLetterheadType};
        const result = await firstValueFrom(this.dialog.open(VatStationeryDialogComponent, {width: '560px', data}).afterClosed());
        return result ?? null;
    }

    private dueDateFor(year: number, periodKey: string, taxProfile: LandlordTaxProfile): Date | null {
        try {
            return computeDueDate({year, periodKey, dauerfrist: taxProfile.dauerfristverlaengerung, bundesland: taxProfile.bundesland, now: new Date()}).dueDateEffective;
        } catch {
            return null;
        }
    }

    private uid(): string {
        return this.authService.authUser?.uid ?? '';
    }
}
